import { useTranslation } from "react-i18next";
import { Mic, MicOff, Video, VideoOff, PhoneOff, ScreenShare, ScreenShareOff } from "lucide-react";
import { Button, cn } from "@tellescope/ui";
import { Tooltip } from "@tellescope/design-system";
import { useVideoCalling } from "../hooks/useVideoCallingContext";
import { useLocalTracks } from "../hooks/useLocalTracks";
import { useScreenShare } from "../hooks/useScreenShare";
import { BackgroundEffectSelector } from "./BackgroundEffectSelector";
import type { VideoControlsProps } from "../types";

/**
 * VideoControls - Control bar for video calls.
 *
 * Provides audio mute, video mute, leave, and end-for-all buttons.
 */
export function VideoControls({ userRole, showEndForAll = false, onLeave, onEndForAll }: VideoControlsProps) {
  const { t } = useTranslation("videoCalling");
  const videoCalling = useVideoCalling();
  const { toggleVideo, toggleAudio } = useLocalTracks();
  const { isScreenSharing, isScreenShareSupported, toggleScreenShare } = useScreenShare();

  const { isVideoEnabled, isAudioEnabled } = videoCalling;

  // Screen sharing is staff-only; patients (enduser) can view a shared screen but not start one.
  const canScreenShare = userRole !== "enduser" && isScreenShareSupported;

  return (
    <div className="flex justify-center items-center gap-2 p-3 bg-gray-900 border-t border-gray-800">
      {/* Audio toggle */}
      <Tooltip>
        <Tooltip.Trigger asChild>
          <Button
            variant="outline"
            size="icon"
            onClick={toggleAudio}
            className={cn(
              "rounded-full h-12 w-12 !bg-gray-800 !border-gray-600 !text-white hover:!bg-gray-700",
              !isAudioEnabled && "!bg-red-500/20 !text-red-400 !border-red-400/50 hover:!bg-red-500/30"
            )}
          >
            {isAudioEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
          </Button>
        </Tooltip.Trigger>
        <Tooltip.Content>{isAudioEnabled ? t("Mute") : t("Unmute")}</Tooltip.Content>
      </Tooltip>

      {/* Video toggle */}
      <Tooltip>
        <Tooltip.Trigger asChild>
          <Button
            variant="outline"
            size="icon"
            onClick={toggleVideo}
            className={cn(
              "rounded-full h-12 w-12 !bg-gray-800 !border-gray-600 !text-white hover:!bg-gray-700",
              !isVideoEnabled && "!bg-red-500/20 !text-red-400 !border-red-400/50 hover:!bg-red-500/30"
            )}
          >
            {isVideoEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
          </Button>
        </Tooltip.Trigger>
        <Tooltip.Content>{isVideoEnabled ? t("Turn off camera") : t("Turn on camera")}</Tooltip.Content>
      </Tooltip>

      {/* Background effect selector (None / Blur / Image) */}
      <BackgroundEffectSelector variant="call" />

      {/* Screen share toggle (staff only) */}
      {canScreenShare && (
        <Tooltip>
          <Tooltip.Trigger asChild>
            <Button
              variant="outline"
              size="icon"
              onClick={toggleScreenShare}
              className={cn(
                "rounded-full h-12 w-12 !bg-gray-800 !border-gray-600 !text-white hover:!bg-gray-700",
                isScreenSharing &&
                  "!bg-emerald-500/20 !text-emerald-300 !border-emerald-400/50 hover:!bg-emerald-500/30"
              )}
            >
              {isScreenSharing ? <ScreenShareOff className="h-5 w-5" /> : <ScreenShare className="h-5 w-5" />}
            </Button>
          </Tooltip.Trigger>
          <Tooltip.Content>{isScreenSharing ? t("Stop sharing") : t("Share screen")}</Tooltip.Content>
        </Tooltip>
      )}

      {/* Leave call */}
      <Button variant="destructive" onClick={onLeave} className="rounded-full h-12 px-6">
        <PhoneOff className="h-5 w-5 mr-2" />
        {t("Leave")}
      </Button>

      {/* End for all - host only */}
      {userRole === "host" && showEndForAll && onEndForAll && (
        <Button
          variant="outline"
          onClick={onEndForAll}
          className="rounded-full h-12 px-4 text-red-400 border-red-400/50 hover:bg-red-500/10 hover:text-red-300"
        >
          {t("End for All")}
        </Button>
      )}
    </div>
  );
}
